import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { resetCurrentDocument } from "slices/addIn/currentDocument/currentDocumentSlice";
import { selectPresenceInstanceId } from "slices/addIn/currentDocument/selectors";
import {
  selectDocumentMetadata,
  selectEffectiveWorkspaceId,
  selectHasSaveTarget,
  selectIsUnlinkedDocument,
} from "slices/addIn/metadata/selectors";
import {
  addToJobQueue,
  removeFromJobQueue,
  setHasUnsavedChanges,
  showGlobalAlert,
} from "slices/app/appSlice";
import { selectCurrentDocument } from "slices/writing/selectors";
import {
  presenceEndpoint,
  presenceParams,
  resolvePresenceTarget,
} from "utils/add-in/editingPresence";
import tokenStorage from "utils/add-in/tokenStorage";

import {
  resetAuthentication,
  setAccessToken,
  setAutosaveEnabled,
  setCurrentUser,
  setIsInitialized,
  setSsoEnabled,
  setTokens,
} from "./addInAuthSlice";
import {
  selectAddInAccessToken,
  selectAddInRefreshToken,
  selectIsAutosaveEnabled,
} from "./selectors";

const AUTH_BASE = "/api/addin/auth";
const USER_SETTINGS_URL = "/api/addin/user-settings";
const DEFAULT_PARAMS = {
  method: "POST",
  headers: { "Content-Type": "application/json" },
};

// Module-level timer ref for proactive refresh — cleared on logout / session expiry.
let refreshTimer = null;

const scheduleTokenRefresh = (expiresInSeconds) => (dispatch) => {
  clearTimeout(refreshTimer);
  // Refresh 60s before expiry; never schedule less than 30s out.
  const delay = Math.max((expiresInSeconds - 60) * 1000, 30_000);
  refreshTimer = setTimeout(() => dispatch(refreshAddInToken()), delay);
};

async function parseAuthError(response) {
  let body = {};
  try {
    body = await response.json();
  } catch {
    // validation 400s may not match the {errorCode, message} shape
  }
  const error = new Error(
    body.message ?? response.statusText ?? "Authentication failed"
  );
  error.errorCode = body.errorCode ?? "UNKNOWN";
  error.status = response.status;
  return error;
}

export const fetchCurrentUser = createAsyncThunk(
  "addIn/authentication/me",
  async (accessToken, { dispatch }) => {
    if (!accessToken) {
      return null;
    }

    dispatch(addToJobQueue("fetchCurrentUser"));
    try {
      const rsp = await fetch(`${AUTH_BASE}/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (rsp.ok) {
        const user = await rsp.json();
        dispatch(setCurrentUser(user));
        return user;
      }
    } catch {
      return null;
    } finally {
      dispatch(removeFromJobQueue("fetchCurrentUser"));
    }
  }
);

export const fetchAddInAuthMode = createAsyncThunk(
  "addIn/authentication/mode",
  async (_, { dispatch }) => {
    try {
      const rsp = await fetch(`${AUTH_BASE}/mode`);
      const ssoEnabled = rsp.ok ? Boolean((await rsp.json()).ssoEnabled) : false;
      dispatch(setSsoEnabled(ssoEnabled));
      return ssoEnabled;
    } catch (err) {
      dispatch(setSsoEnabled(false));
      return false;
    }
  }
);

export const applySignInTokens = (tokens) => async (dispatch) => {
  dispatch(setTokens(tokens));
  await tokenStorage.setRefreshToken(tokens.refreshToken);
  dispatch(scheduleTokenRefresh(tokens.accessTokenExpiresInSeconds));
  dispatch(fetchCurrentUser(tokens.accessToken));
};

export const loginAddIn = createAsyncThunk(
  "addIn/authentication/login",
  async ({ username, password }, { dispatch, rejectWithValue }) => {
    dispatch(addToJobQueue("loginAddIn"));

    const response = await fetch(`${AUTH_BASE}/token`, {
      ...DEFAULT_PARAMS,
      body: JSON.stringify({ username, password }),
    });

    dispatch(removeFromJobQueue("loginAddIn"));

    if (!response.ok) {
      const err = await parseAuthError(response);
      return rejectWithValue({
        errorCode: err.errorCode,
        message: err.message,
      });
    }

    const tokens = await response.json();
    await dispatch(applySignInTokens(tokens));

    return tokens;
  }
);

const OFFICE_DIALOG_ERROR_USER_CLOSED = 12006;
const getSsoDialogSettings = () => ({ height: 60, width: 40, promptBeforeOpen: false });
const SSO_POPUP_ERROR_TEXT =
  "Couldn't open the sign-in window. Please allow pop-ups for this site and try again.";
const SSO_MESSAGE_TYPES = ["VT_SSO_COMPLETE", "VT_SSO_NO_ACCOUNT", "VT_SSO_FAILED"];

const hasOfficeDialogApi = () =>
  Boolean(window.Office?.context?.ui?.displayDialogAsync);

export const loginAddInSso = createAsyncThunk(
  "addIn/authentication/loginSso",
  (_, { dispatch, rejectWithValue }) =>
    new Promise((resolve) => {
      if (!hasOfficeDialogApi()) {

        console.error("VT SSO: Office dialog API unavailable", {
          hasOffice: Boolean(window.Office),
          hasContext: Boolean(window.Office?.context),
          hasUi: Boolean(window.Office?.context?.ui),
        });
        resolve(
          rejectWithValue({ errorCode: "SSO_UNAVAILABLE", message: SSO_POPUP_ERROR_TEXT })
        );
        return;
      }

      const startUrl = `${window.location.origin}/addin-sso/start`;

      const onDialogAsyncResult = (asyncResult) => {
        if (asyncResult.status === window.Office.AsyncResultStatus.Failed) {

          console.error("VT SSO: displayDialogAsync reported Failed", asyncResult.error);
          resolve(
            rejectWithValue({ errorCode: "SSO_FAILED", message: SSO_POPUP_ERROR_TEXT })
          );
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(
          window.Office.EventType.DialogMessageReceived,
          async (arg) => {
            let message;
            try {
              message = JSON.parse(arg.message);
            } catch {
              return;
            }

            if (!SSO_MESSAGE_TYPES.includes(message?.type)) {
              return;
            }

            dialog.close();

            if (message.type === "VT_SSO_NO_ACCOUNT") {
              resolve(rejectWithValue({ errorCode: "NO_ACCOUNT" }));
              return;
            }

            if (message.type === "VT_SSO_FAILED") {
              resolve(
                rejectWithValue({
                  errorCode: "SSO_FAILED",
                  message: message.description || SSO_POPUP_ERROR_TEXT,
                })
              );
              return;
            }

            await dispatch(applySignInTokens(message));
            resolve(message);
          }
        );

        dialog.addEventHandler(
          window.Office.EventType.DialogEventReceived,
          (arg) => {
            if (arg.error === OFFICE_DIALOG_ERROR_USER_CLOSED) {
              resolve({ cancelled: true });
              return;
            }


            console.error("VT SSO: dialog closed with a non-user-initiated error", arg.error);
            resolve(
              rejectWithValue({ errorCode: "SSO_FAILED", message: SSO_POPUP_ERROR_TEXT })
            );
          }
        );
      };

      try {
        window.Office.context.ui.displayDialogAsync(
          startUrl,
          getSsoDialogSettings(),
          onDialogAsyncResult
        );
      } catch (err) {

        console.error("VT SSO: displayDialogAsync threw synchronously", err);
        resolve(
          rejectWithValue({ errorCode: "SSO_FAILED", message: SSO_POPUP_ERROR_TEXT })
        );
      }
    })
);

export const refreshAddInToken = createAsyncThunk(
  "addIn/authentication/refresh",
  async (_, { dispatch, getState }) => {
    const refreshToken = selectAddInRefreshToken(getState());
    if (!refreshToken) {
      dispatch(logoutAddIn());
      return;
    }

    const response = await fetch(`${AUTH_BASE}/refresh`, {
      ...DEFAULT_PARAMS,
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      // 401 INVALID_REFRESH_TOKEN — session ended, send user to login.
      dispatch(logoutAddIn());
      return;
    }

    const tokens = await response.json();
    dispatch(setAccessToken(tokens.accessToken));
    dispatch(scheduleTokenRefresh(tokens.accessTokenExpiresInSeconds));
  }
);

const releasePresenceBestEffort = async (getState) => {
  const state = getState();
  const instanceId = selectPresenceInstanceId(state);
  if (!selectHasSaveTarget(state) || !instanceId) {
    return;
  }

  const isUnlinked = selectIsUnlinkedDocument(state);
  const target = resolvePresenceTarget(
    isUnlinked,
    selectDocumentMetadata(state),
    selectCurrentDocument(state)?.id
  );
  if (!target) {
    return;
  }

  try {
    await localhost.delete(presenceEndpoint(target.config, target.documentId), {
      params: {
        ...presenceParams(
          isUnlinked,
          target.config,
          target.entityId,
          selectEffectiveWorkspaceId(state)
        ),
        instanceId,
      },
      skipAuthRedirect: true,
    });
  } catch (err) {
    console.error(err);
  }
};

export const logoutAddIn = createAsyncThunk(
  "addIn/authentication/logout",
  async (_, { dispatch, getState }) => {
    const accessToken = selectAddInAccessToken(getState());
    dispatch(addToJobQueue("logoutAddIn"));

    await releasePresenceBestEffort(getState);

    try {
      await fetch(`${AUTH_BASE}/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(removeFromJobQueue("logoutAddIn"));

      clearTimeout(refreshTimer);
      refreshTimer = null;

      dispatch(resetAuthentication());
      dispatch(resetCurrentDocument());
      dispatch(setHasUnsavedChanges(false));
      await tokenStorage.clearRefreshToken();
    }
  }
);

const restoreStoredSession = async (dispatch) => {
  const refreshToken = await tokenStorage.getRefreshToken();

  if (!refreshToken) {
    dispatch(setIsInitialized(true));
    return;
  }

  const refreshResponse = await fetch(`${AUTH_BASE}/refresh`, {
    ...DEFAULT_PARAMS,
    body: JSON.stringify({ refreshToken }),
  });

  if (!refreshResponse.ok) {
    await tokenStorage.clearRefreshToken();
    dispatch(resetAuthentication());
    dispatch(setIsInitialized(true));
    return;
  }

  const tokens = await refreshResponse.json();

  // Fetch username before marking as initialized.
  await dispatch(fetchCurrentUser(tokens.accessToken));

  dispatch(setTokens(tokens));
  dispatch(scheduleTokenRefresh(tokens.accessTokenExpiresInSeconds));
  dispatch(setIsInitialized(true));
};

export const initializeAddInSession = createAsyncThunk(
  "addIn/authentication/initialize",
  async (_, { dispatch }) => {
    try {
      await restoreStoredSession(dispatch);
    } catch (error) {
      console.error(error);
      dispatch(resetAuthentication());
    }
  }
);

export const updateAutosavePreference =
  (enabled) => async (dispatch, getState) => {
    const previous = selectIsAutosaveEnabled(getState());
    dispatch(setAutosaveEnabled(enabled));

    try {
      await localhost.put(USER_SETTINGS_URL, { autosaveEnabled: enabled });
    } catch {
      dispatch(setAutosaveEnabled(previous));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: "Could not save your Auto save preference. Please try again.",
        })
      );
    }
  };
